import {
  BaseProps,
  FontSize,
  ForwardProps,
  NoChildrenProp,
  StyledPopover,
  calculateFontSize,
  defaultThemeProp,
} from '@pega/cosmos-react-core';
import dompurify from 'dompurify';
import React from 'react';
import { forwardRef, FunctionComponent, PropsWithoutRef, Ref, useState, useEffect, useMemo } from 'react';
import styled, { css, DefaultTheme } from 'styled-components';

export interface HTMLProps extends BaseProps, NoChildrenProp {
  content: string;
  customTags?: string[];
  ref?: Ref<HTMLDivElement>;
}

export const listStyles = css`
  ul,
  ol {
    padding-inline-start: 2.5rem;
  }

  li {
    margin: 0.5rem 0;
  }

  ul li {
    ul {
      margin-inline-start: 1rem;
      list-style-type: circle;
    }

    ul ul {
      margin-inline-start: 2rem;
      list-style-type: square;
    }

    ul ul ul {
      margin-inline-start: 3rem;
      list-style-type: disc;
    }
  }

  ol li {
    ol {
      margin-inline-start: 1rem;
      list-style-type: lower-alpha;
    }

    ol ol {
      margin-inline-start: 2rem;
      list-style-type: lower-roman;
    }

    ol ol ol {
      margin-inline-start: 3rem;
      list-style-type: decimal;
    }
  }
`;

export const getTableStyles = (theme: DefaultTheme) => css`
  border-collapse: collapse;

  td {
    border: 0.0625rem solid ${theme.base.palette['border-line']};
    min-width: 6.25rem;
  }

  tr:first-child {
    td:first-child {
      border-top-left-radius: 0.125rem;
    }

    td:last-child {
      border-top-right-radius: 0.125rem;
    }
  }

  tr:last-child {
    td:first-child {
      border-bottom-left-radius: 0.125rem;
    }

    td:last-child {
      border-bottom-right-radius: 0.125rem;
    }
  }
`;

export const getHtmlStyles = (theme: DefaultTheme) => {
  const {
    base: { 'font-size': baseFontSize, 'font-scale': baseFontScale, palette },
    components: { text },
  } = theme;
  const fontSize = calculateFontSize(baseFontSize, baseFontScale);
  return `
    ${listStyles}
    table {
      ${getTableStyles(theme)}
    }
    img {
      max-width: 100%;
      height: auto;
      border-radius: 0.25rem;
    }
    a {
      color: ${palette.interactive};
    }
    pre {
      white-space: break-spaces;
    }
    p {
      min-height: ${fontSize[text.primary['font-size'] as FontSize]};
    }
    h1 {
      font-size: ${fontSize[text.h1['font-size'] as FontSize]};
      font-weight: ${text.h1['font-weight']};
      min-height: ${fontSize[text.h1['font-size'] as FontSize]};
    }
    h2 {
      font-size: ${fontSize[text.h2['font-size'] as FontSize]};
      font-weight: ${text.h2['font-weight']};
      min-height: ${fontSize[text.h2['font-size'] as FontSize]};
    }
    h3 {
      font-size: ${fontSize[text.h3['font-size'] as FontSize]};
      font-weight: ${text.h3['font-weight']};
      min-height: ${fontSize[text.h3['font-size'] as FontSize]};
    }
    h4 {
      font-size: ${fontSize[text.h4['font-size'] as FontSize]};
      font-weight: ${text.h4['font-weight']};
      min-height: ${fontSize[text.h4['font-size'] as FontSize]};
    }
    h5 {
      font-size: ${fontSize[text.h5['font-size'] as FontSize]};
      font-weight: ${text.h5['font-weight']};
      min-height: ${fontSize[text.h5['font-size'] as FontSize]};
    }
    h6 {
      font-size: ${fontSize[text.h6['font-size'] as FontSize]};
      font-weight: ${text.h6['font-weight']};
      min-height: ${fontSize[text.h6['font-size'] as FontSize]};
    }
  `;
};

export const StyledHTML = styled.div(({ theme }) => {
  return css`
    width: 100%;
    overflow-x: auto;
    ${getHtmlStyles(theme)}

    ${StyledPopover} ul {
      ul {
        padding-inline-start: 0;
      }
      ol {
        padding-inline-start: 0;
      }
    }

    ${StyledPopover} li {
      margin: 0;
    }
  `;
});

export const sameOrigin = (
  { protocol: protocolA, hostname: hostnameA, port: portA }: Pick<URL, 'protocol' | 'hostname' | 'port'>,
  { protocol: protocolB, hostname: hostnameB, port: portB }: Pick<URL, 'protocol' | 'hostname' | 'port'>,
): boolean => {
  if (protocolA !== protocolB) return false;
  if (hostnameA !== hostnameB) return false;

  if (!portA) {
    if (protocolA === 'http:') portA = '80';
    else if (protocolA === 'https:') portA = '443';
  }

  if (!portB) {
    if (protocolB === 'http:') portB = '80';
    else if (protocolB === 'https:') portB = '443';
  }

  return portA === portB;
};

StyledHTML.defaultProps = defaultThemeProp;

const targetBlankSet = new WeakSet<HTMLAnchorElement>();

dompurify.addHook('beforeSanitizeAttributes', (node) => {
  // Can be passed text nodes
  if (!(node instanceof HTMLElement || node instanceof SVGElement)) return;

  node.removeAttribute('class');

  if (node.tagName === 'A' && node.hasAttribute('href') && node.getAttribute('target') === '_blank') {
    targetBlankSet.add(node as HTMLAnchorElement);
  }
});

dompurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    const href = node.getAttribute('href')!;

    let fullURL: URL;
    try {
      fullURL = new URL(href, window.location.href);
    } catch (e) {
      return;
    }

    if (!sameOrigin(window.location, fullURL)) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener');
    } else if (targetBlankSet.has(node as HTMLAnchorElement)) {
      node.setAttribute('target', '_blank');
      targetBlankSet.delete(node as HTMLAnchorElement);
    }
  }
});

const HTML: FunctionComponent<HTMLProps & ForwardProps> = forwardRef(
  ({ content, customTags, ...restProps }: PropsWithoutRef<HTMLProps>, ref: HTMLProps['ref']) => {
    const [mounted, setMounted] = useState(false);
    const sanitizedHtml = useMemo(() => {
      // Don't render content on the server at all.
      return mounted
        ? dompurify.sanitize(content, {
            CUSTOM_ELEMENT_HANDLING: {
              tagNameCheck: customTags ? (tagName) => customTags.includes(tagName) : null,
            },
          })
        : '';
    }, [content, customTags, mounted]);

    useEffect(() => {
      setMounted(true);
    }, []);

    return <StyledHTML {...restProps} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} ref={ref} />;
  },
);

export default HTML;
